import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, Package, RefreshCw, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function AdminPortalOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/portal/admin/pending-orders`, { withCredentials: true })
      .then(r => setOrders(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const accept = async (id, gst_enabled = false) => {
    setActing(id);
    try {
      const res = await axios.post(`${API}/portal/admin/pending-orders/${id}/accept`, { gst_enabled }, { withCredentials: true });
      toast.success(`Accepted — SO ${res.data?.sales_order?.voucher_no}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(null); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejecting this order? (optional)', '');
    if (reason === null) return;
    setActing(id);
    try {
      await axios.post(`${API}/portal/admin/pending-orders/${id}/reject`, { reason }, { withCredentials: true });
      toast('Order rejected');
      load();
    } catch { toast.error('Failed'); }
    finally { setActing(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-portal-orders">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <Package size={18} className="text-amber-400" /> Unaccepted Portal Orders
            {orders.length > 0 && <span className="text-xs bg-amber-500/15 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/30">{orders.length}</span>}
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Orders placed by clients from the portal. Accept to generate a continuation Sales Order.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800">
          <RefreshCw size={14} />
        </button>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
      {!loading && orders.length === 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
          <CheckCircle2 size={28} className="text-green-500 mx-auto mb-3" />
          <div className="text-zinc-300 text-sm font-medium">Inbox zero — no unaccepted orders</div>
          <div className="text-zinc-500 text-xs mt-1">New orders from clients will show up here.</div>
        </div>
      )}

      <div className="space-y-3">
        {orders.map(o => (
          <div key={o.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`pending-${o.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-zinc-50 text-sm font-semibold">{o.client}</span>
                  <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded-full flex items-center gap-1"><Clock size={8} /> {String(o.created_at).slice(0, 16).replace('T', ' ')}</span>
                </div>
                <div className="text-zinc-500 text-[11px]">Placed by {o.placed_by_name}</div>
                <div className="mt-2 bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`portal-items-${o.id}`}>
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-zinc-900/60">
                        {['Item', 'Code', 'Qty', 'Unit', 'Price', 'Amount'].map(h => (
                          <th key={h} className="text-left text-zinc-500 text-[9px] font-semibold px-2 py-1.5 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(o.items || []).map((it, i) => (
                        <tr key={i} className="border-b border-zinc-800/50 last:border-0">
                          <td className="px-2 py-1 text-zinc-200">{it.product_name}</td>
                          <td className="px-2 py-1 text-zinc-300 font-mono">{it.product_code || '—'}</td>
                          <td className="px-2 py-1 text-zinc-200 font-mono text-right">{it.qty}</td>
                          <td className="px-2 py-1 text-zinc-500 font-mono">{it.unit}</td>
                          <td className="px-2 py-1 text-zinc-300 font-mono text-right">{fmt(it.price)}</td>
                          <td className="px-2 py-1 text-amber-300 font-mono text-right">{fmt((it.qty || 0) * (it.price || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {o.notes && <div className="text-zinc-400 text-[11px] italic mt-2">“{o.notes}”</div>}
                {o.requested_delivery_date && <div className="text-zinc-500 text-[11px] mt-1">Requested by: {o.requested_delivery_date}</div>}
              </div>
              <div className="text-right">
                <div className="text-amber-400 font-mono font-bold text-lg">{fmt(o.total_amount)}</div>
                <div className="flex items-center gap-1.5 mt-2">
                  <button onClick={() => accept(o.id, false)} disabled={acting === o.id} data-testid={`accept-${o.id}`}
                    className="flex items-center gap-1 bg-green-600 hover:bg-green-500 text-white text-xs px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
                    {acting === o.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Accept
                  </button>
                  <button onClick={() => reject(o.id)} disabled={acting === o.id} data-testid={`reject-${o.id}`}
                    className="flex items-center gap-1 bg-zinc-800 hover:bg-red-900/40 text-red-300 text-xs px-3 py-1.5 rounded-lg border border-red-800/40 disabled:opacity-50">
                    <XCircle size={11} /> Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
